import { Link } from "react-router-dom";

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="font-heading text-2xl md:text-3xl font-black mb-4 text-white tracking-tight">
      {title}
    </h2>
    <div className="text-zinc-200 leading-relaxed space-y-3 text-sm md:text-base">{children}</div>
  </section>
);

const StaticShell = ({ title, updated, testId, children }) => (
  <div className="min-h-screen bg-black text-white" data-testid={testId}>
    <header className="border-b border-zinc-800">
      <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="font-mono text-[11px] tracking-[0.3em] uppercase text-zinc-200 hover:text-white">
          ← Money/Hub
        </Link>
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-300">
          Updated {updated}
        </div>
      </div>
    </header>
    <main className="max-w-3xl mx-auto px-6 py-12">
      <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tight mb-10">{title}</h1>
      {children}
    </main>
    <footer className="border-t border-zinc-800 mt-8">
      <div className="max-w-3xl mx-auto px-6 py-8 text-[10px] tracking-[0.3em] uppercase text-zinc-300 font-mono flex flex-wrap gap-4">
        <Link to="/" data-testid="footer-home">Home</Link>
        <Link to="/privacy" data-testid="footer-privacy">Privacy</Link>
        <Link to="/terms" data-testid="footer-terms">Terms</Link>
        <Link to="/dpa" data-testid="footer-dpa">DPA</Link>
        <Link to="/refund" data-testid="footer-refund">Refund</Link>
        <Link to="/msa" data-testid="footer-msa">MSA</Link>
        <Link to="/insurance" data-testid="footer-insurance">Insurance</Link>
        <span className="text-zinc-400">© {new Date().getFullYear()} NC Construction LLC × Clark Properties</span>
      </div>
    </footer>
  </div>
);

/* ------------------------------------------------------------------ */
/* Data Processing Agreement (DPA)                                    */
/* ------------------------------------------------------------------ */
export function DPAPage() {
  return (
    <StaticShell title="Data Processing Agreement" updated="Feb 2026" testId="dpa-page">
      <Section title="1. Parties & Scope">
        <p>
          This Data Processing Agreement (&quot;DPA&quot;) supplements the Terms of Service between
          the customer (&quot;Controller&quot;) and <b className="text-white">NC Construction LLC</b> /{" "}
          <b className="text-white">Clark Properties</b>, operator of Money/Hub (&quot;Processor&quot;).
          It applies whenever Money/Hub processes personal data on the Controller&apos;s behalf.
        </p>
      </Section>

      <Section title="2. Nature & purpose of processing">
        <ul className="list-disc pl-5 space-y-1">
          <li>Providing the Money/Hub SaaS platform (dashboard, alerts, ledger, invoicing).</li>
          <li>Sending transactional and opt-in alert emails via Resend/SMTP.</li>
          <li>Processing platform-fee and marketplace payments via Stripe and PayPal.</li>
          <li>Generating AI-based side-hustle rankings via Claude (Emergent LLM).</li>
        </ul>
      </Section>

      <Section title="3. Categories of data subjects & data">
        <ul className="list-disc pl-5 space-y-1">
          <li><b className="text-white">Subjects:</b> the Controller&apos;s end-users, contractors, and clients.</li>
          <li><b className="text-white">Personal data:</b> email, name, avatar URL, payment handles, earnings amounts, IP address on request, session cookies.</li>
          <li><b className="text-white">Not processed:</b> government ID, health data, biometric data, or card PANs.</li>
        </ul>
      </Section>

      <Section title="4. Processor obligations">
        <ul className="list-disc pl-5 space-y-1">
          <li>Process personal data only on documented instructions from the Controller.</li>
          <li>Maintain confidentiality, tenant isolation (<code>user_id</code> scoping on every query), TLS in transit, and encryption of secrets at rest.</li>
          <li>Notify Controller of any personal-data breach within <b className="text-white">72 hours</b> of confirmation.</li>
          <li>Assist Controller with data subject requests (access, rectification, deletion, portability).</li>
          <li>Delete or return all personal data at end of service, unless legally required to retain.</li>
        </ul>
      </Section>

      <Section title="5. Sub-processors">
        <p>The Processor engages the following sub-processors. The Controller consents to their use:</p>
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li><b className="text-white">Google LLC</b> — OAuth sign-in (EU-US Data Privacy Framework certified).</li>
          <li><b className="text-white">Stripe, Inc.</b> — payments &amp; Connect payouts (PCI-DSS Level 1).</li>
          <li><b className="text-white">PayPal Holdings, Inc.</b> — alternate payment rail.</li>
          <li><b className="text-white">Resend, Inc.</b> / SMTP host — transactional email delivery.</li>
          <li><b className="text-white">MongoDB Atlas</b> (or self-hosted MongoDB) — primary data store.</li>
          <li><b className="text-white">Anthropic PBC</b> (via Emergent LLM router) — AI opportunity ranking.</li>
          <li><b className="text-white">Emergent Labs</b> — hosting &amp; deployment infrastructure.</li>
        </ul>
        <p className="mt-3">The Processor will give 30 days&apos; notice of any new sub-processor. Controller may object; if objection cannot be resolved, either party may terminate.</p>
      </Section>

      <Section title="6. International transfers">
        <p>
          Where personal data is transferred outside the customer&apos;s jurisdiction (e.g., EEA →
          US), the Processor relies on the sub-processor&apos;s <b className="text-white">Standard Contractual Clauses</b>{" "}
          (Google, Stripe, MongoDB Atlas, Anthropic all publish SCCs and/or DPF certifications).
        </p>
      </Section>

      <Section title="7. Security measures">
        <ul className="list-disc pl-5 space-y-1">
          <li>HTTPS/TLS 1.2+ everywhere; HSTS on the public domain.</li>
          <li>HttpOnly, SameSite=Lax session cookies rotated on sign-in.</li>
          <li>Per-user rate limits &amp; signed Stripe/PayPal webhook verification.</li>
          <li>Least-privilege backend service accounts, secrets stored in environment variables (never in source).</li>
          <li>Automated backups of MongoDB; deletion is permanent within 30 days of request.</li>
        </ul>
      </Section>

      <Section title="8. Audit rights">
        <p>
          Once per calendar year (or after a confirmed breach), the Controller may request a written
          summary of the Processor&apos;s technical &amp; organizational measures. On-site audits are
          available for enterprise customers under a mutually signed NDA.
        </p>
      </Section>

      <Section title="9. Term & termination">
        <p>
          This DPA remains in effect for as long as the Processor holds Controller data. Upon
          termination, the Controller may export via <code>/api/user/export</code> and then delete
          the account, wiping all records within seconds.
        </p>
      </Section>

      <Section title="10. Contact / DPO">
        <p>
          Data protection questions:{" "}
          <a className="text-[#60a5fa]" href="mailto:moneyhubvip@outlook.com">
            moneyhubvip@outlook.com
          </a>{" "}
          (Attn: Data Protection).
        </p>
      </Section>
    </StaticShell>
  );
}

/* ------------------------------------------------------------------ */
/* Refund Policy                                                      */
/* ------------------------------------------------------------------ */
export function RefundPage() {
  return (
    <StaticShell title="Refund Policy" updated="Feb 2026" testId="refund-page">
      <Section title="Summary">
        <p>
          We stand behind Money/Hub. If a paid feature genuinely didn&apos;t work as advertised,
          we&apos;ll make it right. Below are the specifics per product line.
        </p>
      </Section>

      <Section title="1. Platform fees (Earnings Ledger 20%)">
        <p>
          The 20% platform fee is charged only after <b className="text-white">you self-report</b>{" "}
          earnings from opportunities discovered through Money/Hub. Because you control the reporting,
          fees paid on accurate reports are <b className="text-white">non-refundable</b>.
        </p>
        <p>
          <b className="text-white">Exception — accidental over-report:</b> if you accidentally logged
          the wrong amount and paid a fee on it, email us within <b className="text-white">14 days</b>{" "}
          with evidence (screenshot of the actual payout) and we&apos;ll refund the delta to your
          original payment method.
        </p>
      </Section>

      <Section title="2. Marketplace payments (Stripe Connect 80/20)">
        <p>
          When a client pays a worker through the in-app marketplace, funds split automatically —
          80% to the worker&apos;s connected bank, 20% platform fee.
        </p>
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li><b className="text-white">Before work starts:</b> full refund on written client + worker agreement, minus Stripe&apos;s non-recoverable processing fee.</li>
          <li><b className="text-white">Work delivered but disputed:</b> we mediate for up to 30 days; if the worker cannot demonstrate delivery, we reverse the 80% and refund the 20% platform fee.</li>
          <li><b className="text-white">Chargebacks:</b> abusive chargebacks are contested with Stripe and may result in account suspension.</li>
        </ul>
      </Section>

      <Section title="3. NC Construction invoices">
        <p>
          Invoices sent via the NC Construction widget are governed by the terms of the underlying
          contracting agreement between NC Construction LLC and the client. Refunds for construction
          services are handled per that agreement; the Money/Hub platform is a payment conduit only.
        </p>
      </Section>

      <Section title="4. Subscription tiers (future)">
        <p>
          If we launch paid subscription tiers, they will include a <b className="text-white">7-day
          money-back guarantee</b> and pro-rated refunds for annual plans cancelled mid-term.
        </p>
      </Section>

      <Section title="5. How to request a refund">
        <ol className="list-decimal pl-5 space-y-1">
          <li>Email <a className="text-[#60a5fa]" href="mailto:moneyhubvip@outlook.com">moneyhubvip@outlook.com</a> with subject &quot;Refund Request&quot;.</li>
          <li>Include the transaction ID (visible in the Earnings Ledger or Stripe/PayPal receipt).</li>
          <li>Attach any supporting evidence.</li>
          <li>We respond within <b className="text-white">3 business days</b>. Approved refunds land in 5–10 business days on the original card.</li>
        </ol>
      </Section>

      <Section title="6. Governing law">
        <p>Refund disputes are governed by the laws of North Carolina, USA (see Terms §Governing Law).</p>
      </Section>
    </StaticShell>
  );
}

/* ------------------------------------------------------------------ */
/* Master Service Agreement (MSA)                                     */
/* ------------------------------------------------------------------ */
export function MSAPage() {
  return (
    <StaticShell title="Master Service Agreement" updated="Feb 2026" testId="msa-page">
      <Section title="1. Purpose">
        <p>
          This Master Service Agreement (&quot;MSA&quot;) governs the professional services provided
          by <b className="text-white">NC Construction LLC</b> and <b className="text-white">Clark Properties</b>{" "}
          (collectively &quot;Provider&quot;) to the client (&quot;Client&quot;). Individual engagements
          are documented via Statements of Work (&quot;SOW&quot;) or invoices that incorporate this MSA
          by reference.
        </p>
      </Section>

      <Section title="2. Services offered">
        <p>Provider offers a range of business and construction services, including but not limited to:</p>
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li><b className="text-white">Project Management</b> — planning, scheduling, coordination, and delivery of client initiatives.</li>
          <li><b className="text-white">Administrative Support</b> — scheduling, correspondence, data entry, document preparation, virtual-assistant work.</li>
          <li><b className="text-white">General Contracting &amp; Property Services</b> — via NC Construction LLC and Clark Properties.</li>
          <li><b className="text-white">Customized Business Services</b> — engagements tailored to the Client&apos;s specific needs and scoped in a separate SOW.</li>
          <li><b className="text-white">Digital &amp; Marketplace Services</b> — offered through the Money/Hub platform (see Terms of Service).</li>
        </ul>
      </Section>

      <Section title="3. Engagement process">
        <ol className="list-decimal pl-5 space-y-1">
          <li>Client submits a request (email, service catalog form, or invoice).</li>
          <li>Provider responds with a scope, timeline, and quote (the &quot;SOW&quot;).</li>
          <li>Client accepts in writing (email or e-signature) and pays any required deposit.</li>
          <li>Work begins per the SOW; changes require written change orders.</li>
        </ol>
      </Section>

      <Section title="4. Fees, invoices, and payment">
        <ul className="list-disc pl-5 space-y-1">
          <li>Fees are stated in each SOW or invoice.</li>
          <li>Standard payment terms: <b className="text-white">Net 15</b> from invoice date unless otherwise agreed.</li>
          <li>Late payments accrue interest at <b className="text-white">1.5% per month</b> or the maximum permitted by law, whichever is lower.</li>
          <li>Payments are collected via Stripe (card, ACH, invoice hosted-pay) or PayPal.</li>
        </ul>
      </Section>

      <Section title="5. Client responsibilities">
        <ul className="list-disc pl-5 space-y-1">
          <li>Provide timely access to information, materials, and decision-makers.</li>
          <li>Review and respond to deliverables within reasonable timelines.</li>
          <li>Ensure all information supplied is accurate and lawful to share.</li>
        </ul>
      </Section>

      <Section title="6. Intellectual property">
        <p>
          Upon full payment, Client receives a perpetual, non-exclusive license to use deliverables
          for their intended business purpose. Provider retains ownership of pre-existing tools,
          templates, methodologies, and reusable components.
        </p>
      </Section>

      <Section title="7. Confidentiality">
        <p>
          Each party will hold the other&apos;s Confidential Information in strict confidence and
          use it solely to perform under this MSA. Obligations survive termination for{" "}
          <b className="text-white">3 years</b>.
        </p>
      </Section>

      <Section title="8. Warranties & disclaimers">
        <p>
          Provider warrants that services will be performed in a professional and workmanlike manner.
          EXCEPT AS EXPRESSLY STATED, SERVICES ARE PROVIDED &quot;AS IS&quot; AND ALL OTHER
          WARRANTIES ARE DISCLAIMED TO THE FULLEST EXTENT PERMITTED BY LAW.
        </p>
      </Section>

      <Section title="9. Limitation of liability">
        <p>
          To the maximum extent permitted by law, Provider&apos;s total liability under any SOW is
          limited to the amount actually paid by Client for the services giving rise to the claim in
          the <b className="text-white">12 months</b> preceding the event. Neither party is liable
          for indirect, incidental, consequential, or punitive damages.
        </p>
      </Section>

      <Section title="10. Indemnification">
        <p>
          Each party will indemnify the other against third-party claims arising from its own gross
          negligence, willful misconduct, or breach of this MSA.
        </p>
      </Section>

      <Section title="11. Insurance">
        <p>
          Provider maintains commercially reasonable general liability and, where applicable, cyber
          liability insurance. Certificates are available on request. See our{" "}
          <Link to="/insurance" className="text-[#60a5fa] underline">Cyber Liability Insurance</Link> page.
        </p>
      </Section>

      <Section title="12. Term & termination">
        <p>
          Either party may terminate this MSA for material breach with 30 days&apos; written notice
          (10 days for non-payment). Termination does not excuse fees for services already rendered.
        </p>
      </Section>

      <Section title="13. Governing law & disputes">
        <p>
          This MSA is governed by the laws of the <b className="text-white">State of North Carolina, USA</b>.
          Disputes will be resolved by binding arbitration in Cherokee County, NC, except that
          either party may seek injunctive relief in court to protect intellectual property or
          confidential information.
        </p>
      </Section>

      <Section title="14. General">
        <ul className="list-disc pl-5 space-y-1">
          <li>Independent contractors — no employment or partnership is created.</li>
          <li>Entire agreement — this MSA plus any SOW is the complete agreement.</li>
          <li>No waiver by course of dealing.</li>
          <li>Force majeure — neither party liable for delays due to events beyond reasonable control.</li>
        </ul>
      </Section>

      <Section title="Contact">
        <p>
          <a className="text-[#60a5fa]" href="mailto:moneyhubvip@outlook.com">
            moneyhubvip@outlook.com
          </a>
        </p>
      </Section>
    </StaticShell>
  );
}

/* ------------------------------------------------------------------ */
/* Cyber Liability Insurance Disclosure                               */
/* ------------------------------------------------------------------ */
export function InsurancePage() {
  return (
    <StaticShell title="Cyber Liability Insurance" updated="Feb 2026" testId="insurance-page">
      <Section title="Our commitment">
        <p>
          <b className="text-white">NC Construction LLC</b> and <b className="text-white">Clark Properties</b>{" "}
          take data security seriously. In addition to the technical safeguards described in our{" "}
          <Link to="/dpa" className="text-[#60a5fa] underline">DPA</Link> and{" "}
          <Link to="/privacy" className="text-[#60a5fa] underline">Privacy Policy</Link>, we maintain
          business insurance appropriate to our operations.
        </p>
      </Section>

      <Section title="Coverage overview">
        <ul className="list-disc pl-5 space-y-1">
          <li><b className="text-white">General Liability</b> — construction &amp; property operations (NC Construction LLC / Clark Properties).</li>
          <li><b className="text-white">Cyber Liability</b> — covering data-breach response, notification costs, forensic investigation, and third-party claims arising from a security incident on the Money/Hub platform.</li>
          <li><b className="text-white">Errors &amp; Omissions</b> — professional services liability for project-management and administrative-support engagements.</li>
        </ul>
        <p className="mt-3 text-xs text-zinc-200">
          Coverage limits and carriers may change from time to time. Enterprise clients may request a
          current Certificate of Insurance (COI) naming their entity as an additional insured where
          appropriate.
        </p>
      </Section>

      <Section title="Incident response commitments">
        <ul className="list-disc pl-5 space-y-1">
          <li>Confirmed personal-data breaches are reported to affected customers within <b className="text-white">72 hours</b>.</li>
          <li>Post-incident report includes root cause, scope of impact, remediation, and preventive measures.</li>
          <li>Affected users are offered free identity-monitoring credits where legally required.</li>
        </ul>
      </Section>

      <Section title="Requesting a Certificate of Insurance">
        <p>
          Enterprise, government, or procurement customers can request a COI by emailing{" "}
          <a className="text-[#60a5fa]" href="mailto:moneyhubvip@outlook.com">
            moneyhubvip@outlook.com
          </a>{" "}
          with subject &quot;COI Request&quot; and the entity name to be named as additional
          insured. Typical turnaround: 3–5 business days.
        </p>
      </Section>

      <Section title="Disclaimer">
        <p className="text-xs text-zinc-200">
          This page is a summary for informational purposes and does not, by itself, create insurance
          coverage or a contractual obligation. Actual coverage is governed by the terms, conditions,
          and exclusions of the underlying insurance policies in force at the time of a claim.
        </p>
      </Section>
    </StaticShell>
  );
}
